import { useCallback, useMemo } from 'react'

import { NetworkStatus } from '@apollo/client'
import { useQuery } from '@apollo/client/react'
import type {
  OperationVariables,
  ApolloClient,
  ErrorLike,
} from '@apollo/client'

import { toast } from '@buffer-mono/popcorn'

import { useOrganizationId } from '~publish/contexts/AccountContext'
import { graphql, readFragment } from '~publish/graphql'
import type { ResultOf, VariablesOf } from '~publish/graphql'
import { debugLog } from '~publish/helpers/debugLog'
import { useInfiniteScrollPagination } from '~publish/hooks/useInfiniteScrollPagination'
import { useRealTimePusherUpdates } from '~publish/pages/AllChannels/PostList/useRealTimePusherUpdates'
import { CommentsPostCard_Post } from '~publish/pages/Community/fragments/CommentsPostCard_Post'
import type { PostStatus } from '~publish/pages/Community/graphqlEnums'

export const POSTS_PER_PAGE = 20
export const MAX_POSTS_REFETCH_LIMIT = 300

/**
 * Lightweight query for posts list sidepanel
 * Uses CommentsPostCard_Post fragment for data consistency
 */
export const GetPostsList = graphql(
  `
    query GetPostsList(
      $first: Int!
      $after: String
      $organizationId: OrganizationId!
      $channelIds: [ChannelId!]
      $status: [PostStatus!]
      $sort: [PostSortInput!]
    ) {
      posts(
        input: {
          organizationId: $organizationId
          filter: { channelIds: $channelIds, status: $status }
          sort: $sort
        }
        first: $first
        after: $after
      ) {
        edges {
          node {
            ...CommentsPostCard_Post
            # Additional fields for list display
            createdAt
            sentAt
          }
        }
        pageInfo {
          startCursor
          endCursor
          hasPreviousPage
          hasNextPage
        }
      }
    }
  `,
  [CommentsPostCard_Post],
)

type GetPostsListData = ResultOf<typeof GetPostsList>

// Input types only exist on the variables side, so derive the sort element type
// from the query's `sort` variable rather than from a result selection.
type PostSortInput = NonNullable<
  NonNullable<VariablesOf<typeof GetPostsList>['sort']>[number]
>

export type UsePostsListQueryOptions = {
  channelId?: string
  channelIds?: string[]
  status?: PostStatus[]
  sortDirection?: 'asc' | 'desc'
  perPage?: number
  skip?: boolean
}

const getDefaultSort = (
  sortDirection: 'asc' | 'desc' = 'desc',
): PostSortInput[] => [
  { field: 'dueAt', direction: sortDirection },
  { field: 'createdAt', direction: sortDirection },
]

export type UsePostsListQueryResult = {
  posts: NonNullable<GetPostsListData['posts']['edges']>[number]['node'][]
  loading: boolean
  fetchingMore: boolean
  error: ErrorLike | undefined
  lastElementRef: (node?: Element | null) => void

  refetch: (
    variables?: Partial<OperationVariables>,
  ) => Promise<ApolloClient.QueryResult<unknown>>
  hasNextPage: boolean
  /** Load next page of posts. Used by infinite scroll and by auto-fetch when opening a post by URL. */
  fetchMore: () => Promise<void>
}

export const usePostsListQuery = ({
  channelId,
  channelIds,
  status = ['sent'],
  sortDirection = 'desc',
  perPage = POSTS_PER_PAGE,
  skip: skipQuery,
}: UsePostsListQueryOptions): UsePostsListQueryResult => {
  const organizationId = useOrganizationId()

  // Resolve channel filter: channelIds array > single channelId > all channels
  const resolvedChannelIds =
    channelIds && channelIds.length > 0
      ? channelIds
      : channelId
        ? [channelId]
        : undefined

  const {
    data,
    dataState,
    refetch,
    fetchMore,
    error,
    networkStatus,
    variables,
  } = useQuery(GetPostsList, {
    variables: {
      first: perPage,
      after: null,
      organizationId: organizationId || '',
      channelIds: resolvedChannelIds,
      status,
      sort: getDefaultSort(sortDirection),
    },
    fetchPolicy: 'cache-and-network',
    // cache-first (not cache-only) so `fetchMore` pagination can reach the
    // network; cache-only still serves cached pages without re-fetching.
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    errorPolicy: 'all',
    skip: !organizationId || skipQuery,
  })

  const loading =
    networkStatus === NetworkStatus.loading ||
    networkStatus === NetworkStatus.refetch ||
    networkStatus === NetworkStatus.setVariables
  const fetchingMore = networkStatus === NetworkStatus.fetchMore

  if (networkStatus === NetworkStatus.refetch) {
    debugLog('🔄 refetching posts...')
  }

  useRealTimePusherUpdates({
    filters: {
      status,
      tagIds: undefined,
      channelIds: resolvedChannelIds,
    },
    variables,
    refetch: async (options): Promise<void> => {
      debugLog('⚡ posts refetch triggered', options?.mode)

      const insertDelta =
        options?.mode === 'insert' ? Math.max(1, options.insertCount ?? 1) : 0

      await refetch({
        first:
          // HACK: fetch as many more posts as were inserted in case of insert
          // mode this works up to a limit, but it helps resolving issue of
          // new posts not showing up
          data?.posts?.edges?.length
            ? // HACK: we currently have very high limits on the number of posts we can fetch
              // so we need to limit the number of posts we fetch to avoid overwhelming the server
              Math.min(
                data.posts.edges.length + insertDelta,
                MAX_POSTS_REFETCH_LIMIT,
              )
            : POSTS_PER_PAGE,
      })
    },
    isPostVisible: (id: string): boolean => {
      return data?.posts?.edges
        ? data.posts.edges.some((edge) => {
            const post = readFragment(CommentsPostCard_Post, edge.node)
            return post.id === id
          })
        : false
    },
    sort: 'mostRecentlyPostedFirst',
  })

  const loadMorePosts = useCallback(async () => {
    if (!data?.posts?.pageInfo?.hasNextPage) return
    try {
      await fetchMore({
        variables: {
          after: data.posts.pageInfo.endCursor,
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult?.posts?.edges) return prev

          const newEdges = fetchMoreResult.posts.edges
          const newPostIds = new Set(
            newEdges.map((edge) => {
              const post = readFragment(CommentsPostCard_Post, edge.node)
              return post.id
            }),
          )
          const existingEdges = (prev.posts?.edges || []).filter((edge) => {
            const post = readFragment(CommentsPostCard_Post, edge.node)
            return !newPostIds.has(post.id)
          })

          const updatedEdges = [...existingEdges, ...newEdges]

          return {
            ...prev,
            posts: {
              ...prev.posts,
              edges: updatedEdges,
              pageInfo: fetchMoreResult.posts.pageInfo,
            },
          }
        },
      })
    } catch (fetchMorePostsError) {
      toast.error('Error fetching more posts')
      debugLog('❌ Failed to fetch more posts:', fetchMorePostsError)
    }
  }, [
    data?.posts?.pageInfo?.hasNextPage,
    data?.posts?.pageInfo?.endCursor,
    fetchMore,
  ])

  const [lastElementRef] = useInfiniteScrollPagination({
    loading: fetchingMore,
    hasNextPage: data?.posts?.pageInfo?.hasNextPage,
    fetchMore: loadMorePosts,
  })

  const completeData = dataState === 'complete' ? data : undefined

  const posts = useMemo(() => {
    const edges = completeData?.posts?.edges || []
    return edges
      .filter((edge) => {
        const post = readFragment(CommentsPostCard_Post, edge.node)
        return post.notificationStatus !== 'notified'
      })
      .map((edge) => edge.node)
  }, [completeData?.posts?.edges])

  return useMemo(
    () => ({
      posts,
      loading,
      fetchingMore,
      error,
      lastElementRef,
      refetch,
      hasNextPage: data?.posts?.pageInfo?.hasNextPage ?? false,
      fetchMore: loadMorePosts,
    }),
    [
      posts,
      loading,
      fetchingMore,
      error,
      lastElementRef,
      refetch,
      data?.posts?.pageInfo?.hasNextPage,
      loadMorePosts,
    ],
  )
}
